<script setup lang="ts">
  import { defineProps, computed, watch, onMounted, ref } from 'vue'
  import {
    TrashIcon,
    Eye as EyeIcon,
    Plus as PlusIcon,
    CheckCircle2,
    CircleOff,
    Hash,
    Calendar,
    Mail,
    Phone,
    Type,
    AlignLeft,
    Upload,
    Link,
    GripHorizontal,
    Copy,
    FolderTree,
    Info
  } from 'lucide-vue-next'
  import {
    type SuperUserQuestionnaire as Questionnaire,
    type QuestionnaireSection,
    type QuestionnaireQuestion,
    FieldTypeEnum,
    type GenericDataMapper,
    TagRelationshipEnum,
    QuestionnaireSectionTypeEnum
  } from '@/types/generated'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { Switch } from '@/components/ui/switch'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import { type Option } from '@/components/admin/form/SelectField.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  interface InputTypeOption extends Option {
    supportsLogicJump?: boolean
  }
  import { useTranslation } from '@/composables/useTranslation'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import SingleChoice from '@/components/icons/SingleChoice.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import QuestionPreviewDialog from './QuestionPreviewDialog.vue'
  import ConnectedFieldSelect from './ConnectedFieldSelect.vue'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'

  const { t, locale } = useTranslation({
    en: {
      required: 'Required',
      helpText: 'Help text (Optional)',
      autofill: 'Autofill (Optional)',
      autofillDescription:
        'If corresponding data is available in the case, this question will be filled automatically',
      inputType: 'Input type',
      notSelected: 'Not selected',
      preview: 'Preview',
      duplicate: 'Duplicate',
      moveToSection: 'Move to section',
      delete: 'Delete',
      addOption: 'Add option',
      logicJump: 'Logic jump',
      switchToMultipleChoice: 'Switch to multiple choice to add more options',
      goToQuestion: 'Go to question...',
      shortText: 'Short text',
      booleanYesNo: 'Boolean (Yes/No)',
      singleChoice: 'Single choice',
      multipleChoice: 'Multiple choice',
      fileUpload: 'File upload',
      date: 'Date',
      yes: 'Yes',
      no: 'No',
      facingLegalAction: 'Facing legal action',
      consideringLegalAction: 'Considering initiating legal action',
      seekingLegalAdvice: 'Seeking legal advice only',
      other: 'Other',
      goToUploadDocuments: 'Go to: Upload relevant documents',
      goToWhenNeedAssistance: 'Go to: When do you need legal assistance?',
      email: 'Email',
      phone: 'Phone',
      whatsapp: 'WhatsApp',
      fax: 'Fax',
      mail: 'Mail',
      helpTextTooltip: 'Additional context shown to users when filling out this question',
      connectedFieldTooltip:
        'Connect this question to a case data field for automatic data mapping' +
        'If corresponding data is available in the case, this question will be filled automatically',
      connectedField: 'Connected field',
      participant: 'Participant',
      participantTooltip: 'Select which type of participant this question relates to',
      questionTitle: 'Question title',
      linkedFieldTooltip: 'Connected to case data field',
      unlinkedFieldTooltip: 'Not connected to any case data field',
      supportsLogicJump: 'Supports logic jump',
      number: 'Number',
      shortTextOneLine: 'Short text (one line)',
      longTextMultipleLines: 'Long text (multiple lines)',
      url: 'URL',
      choicePlaceholder: 'Choice {number}'
    },
    de: {
      required: 'Erforderlich',
      helpText: 'Hilfstext (Optional)',
      autofill: 'Autofill (Optional)',
      autofillDescription:
        'Falls entsprechende Daten im Fall verfügbar sind, wird diese Frage automatisch ausgefüllt',
      inputType: 'Eingabetyp',
      notSelected: 'Nicht ausgewählt',
      preview: 'Vorschau',
      duplicate: 'Duplizieren',
      moveToSection: 'In Abschnitt verschieben',
      delete: 'Löschen',
      addOption: 'Option hinzufügen',
      logicJump: 'Logik-Sprung',
      switchToMultipleChoice: 'Zu Mehrfachauswahl wechseln, um weitere Optionen hinzuzufügen',
      goToQuestion: 'Zu Frage gehen...',
      shortText: 'Kurzer Text',
      booleanYesNo: 'Boolean (Ja/Nein)',
      singleChoice: 'Einzelauswahl',
      multipleChoice: 'Mehrfachauswahl',
      fileUpload: 'Datei-Upload',
      date: 'Datum',
      yes: 'Ja',
      no: 'Nein',
      facingLegalAction: 'Mit rechtlichen Schritten konfrontiert',
      consideringLegalAction: 'Erwägt rechtliche Schritte einzuleiten',
      seekingLegalAdvice: 'Nur rechtliche Beratung suchen',
      other: 'Andere',
      goToUploadDocuments: 'Gehe zu: Relevante Dokumente hochladen',
      goToWhenNeedAssistance: 'Gehe zu: Wann benötigen Sie rechtliche Unterstützung?',
      email: 'E-Mail',
      phone: 'Telefon',
      whatsapp: 'WhatsApp',
      fax: 'Fax',
      mail: 'Post',
      helpTextTooltip:
        'Zusätzlicher Kontext, der Benutzern beim Ausfüllen dieser Frage angezeigt wird',
      connectedField: 'Verknüpftes Feld',
      connectedFieldTooltip:
        'Verbinden Sie diese Frage mit einem Falldatenfeld für automatische Datenzuordnung',
      participant: 'Beteiligter',
      participantTooltip: 'Wählen Sie aus, auf welchen Beteiligtentyp sich diese Frage bezieht',
      questionTitle: 'Fragetitel',
      linkedFieldTooltip: 'Mit Falldatenfeld verbunden',
      unlinkedFieldTooltip: 'Nicht mit einem Falldatenfeld verbunden',
      supportsLogicJump: 'Unterstützt Logik-Sprung',
      number: 'Zahl',
      shortTextOneLine: 'Kurzer Text (eine Zeile)',
      longTextMultipleLines: 'Langer Text (mehrere Zeilen)',
      url: 'URL',
      choicePlaceholder: '{number}. Wahl'
    }
  })

  const questionnaireEditorStore = useQuestionnaireEditorStore()

  const tags = computed<GenericDataMapper[]>(() => questionnaireEditorStore.tags)

  // Get all participant types with their translations (like in UserQuestionnaireSection.vue)
  const participantTypeOptions = computed<Option[]>(() => {
    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN

    return Object.values(TagRelationshipEnum)
      .map((value) => ({
        value: value,
        title: entityTranslations[value as keyof typeof entityTranslations] || value
      }))
      .sort((a, b) => a.title.localeCompare(b.title))
  })
  const selectedTag = computed<GenericDataMapper | null>(() => {
    return tags.value.find((tag) => tag.key === question.value?.tag) ?? null
  })

  // Track the previous tag value to detect actual tag changes vs question switches
  const previousTagKey = ref<string | null | undefined>(undefined)

  const props = defineProps({
    uuid: {
      type: String,
      required: true
    },
    sectionUuid: {
      type: String,
      required: true
    },
    questionnaire: {
      type: Object as () => Questionnaire,
      required: true
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  })

  const section = computed<QuestionnaireSection | undefined>(() =>
    props.questionnaire?.schema.find((p) => p.uuid === props.sectionUuid)
  )
  const question = computed<QuestionnaireQuestion | undefined>(() =>
    section.value?.items.find((q) => q.uuid === props.uuid)
  )
  const showLogicJump = computed(() => {
    return (
      question.value?.type === FieldTypeEnum.SELECT || question.value?.type === FieldTypeEnum.YESNO
    )
  })

  const jumpToOptions = computed<Option[]>(() => {
    const options = (props.questionnaire?.schema ?? [])
      .flatMap((section) => {
        return section.items.map((q) => ({
          ...q,
          sectionTitle: section.title
        }))
      })
      .filter((q) => q.uuid !== props.uuid)
      .map((q) => ({
        value: q.uuid,
        title: `${q.sectionTitle} | ${q.label}`
      })) as Option[]
    return [...options, { value: 'END', title: 'End of questionnaire' }]
  })

  const updateOption = (newValue: string, index: number) => {
    if (question.value?.choices) {
      question.value.choices[index] = newValue
      if (question.value.jump_to !== null) {
        const updatedJumpTo = [...question.value.jump_to]
        updatedJumpTo[index] = { ...updatedJumpTo[index], value: newValue }
        Object.assign(question.value, { jump_to: updatedJumpTo })
      }
    }
  }
  const updateJumpTo = (newValue: string, index: number) => {
    if (question.value && question.value.jump_to && question.value.jump_to[index]) {
      const updatedJumpTo = [...question.value.jump_to]
      updatedJumpTo[index] = { ...updatedJumpTo[index], uuid: newValue }
      Object.assign(question.value, { jump_to: updatedJumpTo })
    }
  }

  const isJumpToEnabled = computed(() => {
    return question.value?.jump_to !== null
  })

  const handleSwitchChange = (checked: boolean) => {
    if (question.value?.choices) {
      if (checked) {
        const newJumpTo = question.value.choices.map((choice) => ({
          value: choice,
          uuid: null
        }))
        Object.assign(question.value, { jump_to: newJumpTo })
      } else {
        Object.assign(question.value, { jump_to: null })
      }
    }
  }
  const addOption = () => {
    if (question.value?.choices) {
      question.value.choices.push('')
      if (question.value.jump_to !== null) {
        const updatedJumpTo = [...question.value.jump_to, { value: '', uuid: null }]
        Object.assign(question.value, { jump_to: updatedJumpTo })
      }
    }
  }
  const removeOption = (index: number) => {
    if (question.value?.choices) {
      question.value.choices.splice(index, 1)
      if (question.value.jump_to !== null) {
        const updatedJumpTo = [...question.value.jump_to]
        updatedJumpTo.splice(index, 1)
        Object.assign(question.value, { jump_to: updatedJumpTo })
      }
    }
  }

  const initYesNoChoices = () => {
    if (!question.value) return
    if (!question.value.choices) {
      question.value.choices = []
    }

    if (question.value.type === FieldTypeEnum.YESNO) {
      if (!question.value.choices.length) {
        question.value.choices = ['Yes', 'No']
      }
      const existingJumpTo = question.value.jump_to
      if (existingJumpTo) {
        const newJumpTo = [
          { value: 'Yes', uuid: existingJumpTo[0]?.uuid ?? null },
          { value: 'No', uuid: existingJumpTo[1]?.uuid ?? null }
        ]
        Object.assign(question.value, { jump_to: newJumpTo })
      }
    }
  }

  watch(
    () => question.value?.type,
    (newType, oldType) => {
      if (
        newType === FieldTypeEnum.YESNO ||
        (newType === FieldTypeEnum.SELECT && newType !== oldType)
      ) {
        initYesNoChoices()
      }
    }
  )

  onMounted(() => {
    setTimeout(() => {
      initYesNoChoices()
    }, 0)
  })

  // Watch for question changes to reset state tracking
  watch(
    () => props.uuid,
    (newUuid) => {
      // Reset the previous tag key when switching questions
      // This prevents the tag watcher from triggering on question switch
      previousTagKey.value = undefined
    },
    { immediate: true }
  )

  // Watch for actual tag changes (when user changes the connected field)
  // This should NOT trigger when switching between questions
  watch(
    () => question.value?.tag as string | null | undefined,
    (newTagKey) => {
      if (!question.value) {
        return
      }

      // Normalize the tag key value
      const normalizedNewTagKey = newTagKey || null

      // Skip if previousTagKey is undefined (initial load or just switched questions)
      if (previousTagKey.value === undefined) {
        // Initialize the previous tag key for future comparisons
        previousTagKey.value = normalizedNewTagKey
        return
      }

      // Check if the tag actually changed
      const tagChanged = normalizedNewTagKey !== previousTagKey.value

      // Update the previous tag key
      previousTagKey.value = normalizedNewTagKey

      // Only proceed if the tag actually changed (user changed the connected field)
      if (!tagChanged) {
        return
      }

      // Now handle the actual tag change
      if (newTagKey) {
        const tag = tags.value.find((t) => t.key === newTagKey)
        if (tag) {
          question.value!.type = tag.field_type

          if (tag.field_type !== FieldTypeEnum.YESNO || (tag.choices && tag.choices.length > 0)) {
            question.value!.choices = JSON.parse(JSON.stringify(tag.choices || []))
            question.value!.jump_to = null
          }

          question.value!.starts_ai_analysis = tag.starts_ai_analysis
        }
      } else {
        question.value!.starts_ai_analysis = false
      }
    }
  )

  // Auto-set tag_relationship based on section type
  watch(
    () => [(section.value as any)?.type, (section.value as any)?.participant_type],
    ([sectionType, participantType]) => {
      if (!question.value) return

      if (sectionType === QuestionnaireSectionTypeEnum.PARTICIPANT && participantType) {
        // Auto-set tag_relationship to match section's participant_type
        question.value.tag_relationship = participantType
      } else if (sectionType === QuestionnaireSectionTypeEnum.LAW_SPECIFIC) {
        // Clear tag_relationship for law-specific sections
        question.value.tag_relationship = null
      }
    },
    { immediate: true }
  )

  const isTagRelationshipReadonly = computed(() => {
    return (section.value as any)?.type === QuestionnaireSectionTypeEnum.PARTICIPANT
  })

  const isEntityTag = computed(() => {
    return selectedTag.value?.is_entity_field ?? false
  })

  const sectionType = computed(() => {
    return (section.value as any)?.type || QuestionnaireSectionTypeEnum.LAW_SPECIFIC
  })

  const isParticipantSection = computed(() => {
    return sectionType.value === QuestionnaireSectionTypeEnum.PARTICIPANT
  })

  const questionnaireLawField = computed(() => {
    return props.questionnaire?.law_field || null
  })

  // Auto-set tag_number to 1 for all questions
  // For participant sections, the store will override this with instanceNumber
  watch(
    () => question.value,
    (newQuestion) => {
      if (newQuestion && newQuestion.tag_number === null) {
        newQuestion.tag_number = 1
      }
    },
    { immediate: true }
  )

  const inputTypeOptions = computed<InputTypeOption[]>(() => [
    {
      value: FieldTypeEnum.SELECT,
      title: t('singleChoice'),
      icon: SingleChoice,
      supportsLogicJump: true
    },
    {
      value: FieldTypeEnum.MULTISELECT,
      title: t('multipleChoice'),
      icon: CheckCircle2
    },
    {
      value: FieldTypeEnum.YESNO,
      title: t('booleanYesNo'),
      icon: CircleOff,
      supportsLogicJump: true
    },
    {
      value: FieldTypeEnum.NUMBERINPUT,
      title: t('number'),
      icon: Hash
    },
    {
      value: FieldTypeEnum.DATEFIELD,
      title: t('date'),
      icon: Calendar
    },
    {
      value: FieldTypeEnum.EMAILINPUT,
      title: t('email'),
      icon: Mail
    },
    {
      value: FieldTypeEnum.PHONEINPUT,
      title: t('phone'),
      icon: Phone
    },
    {
      value: FieldTypeEnum.TEXTINPUT,
      title: t('shortTextOneLine'),
      icon: Type
    },
    {
      value: FieldTypeEnum.TEXTAREA,
      title: t('longTextMultipleLines'),
      icon: AlignLeft
    },
    {
      value: FieldTypeEnum.FILEFIELD,
      title: t('fileUpload'),
      icon: Upload
    },
    {
      value: FieldTypeEnum.URLINPUT,
      title: t('url'),
      icon: Link
    }
  ])

  const getQuestionTag = (question: QuestionnaireQuestion | undefined): string => {
    return (question?.tag as string) || ''
  }

  const setQuestionTag = (question: QuestionnaireQuestion | undefined, value: string) => {
    if (question) {
      question.tag = value || null
    }
  }

  const availableSections = computed(() => {
    return (
      props.questionnaire.schema?.map((section) => ({
        value: section.uuid,
        title: `${section.title || 'Untitled Section'} (${section.items?.length || 0} ${(section.items?.length || 0) === 1 ? 'question' : 'questions'})`
      })) ?? []
    )
  })

  const searchQuery = ref('')

  const availableSectionsForMove = computed(() => {
    const currentSectionUuid = props.sectionUuid
    let filteredSections = availableSections.value.filter(
      (section) => section.value !== currentSectionUuid
    )

    // Apply search filter if there's a search query
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase()
      filteredSections = filteredSections.filter((section) =>
        section.title.toLowerCase().includes(query)
      )
    }

    return filteredSections
  })

  const duplicateQuestion = () => {
    if (section.value && question.value) {
      questionnaireEditorStore.copyQuestion(section.value, question.value)
    }
  }

  const deleteQuestion = () => {
    if (section.value && question.value) {
      questionnaireEditorStore.removeQuestion(section.value, question.value)
    }
  }

  const moveQuestionToSection = (targetSectionUuid: string) => {
    if (section.value && question.value && targetSectionUuid) {
      const targetSection = props.questionnaire.schema?.find((s) => s.uuid === targetSectionUuid)
      if (targetSection) {
        questionnaireEditorStore.moveQuestionToSection(section.value, targetSection, question.value)
      }
    }
  }
  const draggedOptionIndex = ref<number | null>(null)
  const dragOverOptionIndex = ref<number | null>(null)

  const onOptionDragStart = (event: DragEvent, index: number) => {
    draggedOptionIndex.value = index
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', index.toString())
    }
  }

  const onOptionDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverOptionIndex.value = index
  }

  const onOptionDragLeave = () => {
    dragOverOptionIndex.value = null
  }

  const onOptionDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()

    if (
      draggedOptionIndex.value !== null &&
      draggedOptionIndex.value !== targetIndex &&
      question.value
    ) {
      const choices = [...(question.value.choices || [])]
      const jumpTo = question.value.jump_to ? [...question.value.jump_to] : null

      const [movedChoice] = choices.splice(draggedOptionIndex.value, 1)
      choices.splice(targetIndex, 0, movedChoice)

      if (jumpTo) {
        const [movedJumpTo] = jumpTo.splice(draggedOptionIndex.value, 1)
        jumpTo.splice(targetIndex, 0, movedJumpTo)
        question.value.jump_to = jumpTo
      }

      question.value.choices = choices
    }

    draggedOptionIndex.value = null
    dragOverOptionIndex.value = null
  }

  const onOptionDragEnd = () => {
    draggedOptionIndex.value = null
    dragOverOptionIndex.value = null
  }

  // Preview dialog state
  const isPreviewOpen = ref(false)

  const openPreview = () => {
    isPreviewOpen.value = true
  }
</script>

<template>
  <div
    v-if="section && question"
    class="flex w-full flex-col items-start justify-start self-stretch overflow-hidden"
  >
    <!-- Question Content -->
    <div class="flex w-full flex-col items-start justify-start gap-4 self-stretch">
      <!-- Connected Field -->
      <div class="flex w-full flex-col items-start justify-start gap-1 self-stretch">
        <!-- Labels row (side by side on larger screens) -->
        <div class="flex w-full flex-col gap-1 self-stretch sm:flex-row sm:items-start sm:gap-3">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5">
              <label class="text-sm font-medium text-gray-700">
                {{ t('connectedField') }}
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <button
                      type="button"
                      class="inline-flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <Info class="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    variant="secondary"
                    :show-arrow="true"
                    class="max-w-xs"
                  >
                    {{ t('connectedFieldTooltip') }}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div
            v-if="isParticipantSection"
            class="min-w-0 flex-1"
          >
            <div class="flex items-center gap-1.5">
              <label class="text-sm font-medium text-gray-700">
                {{ t('participant') }}
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <button
                      type="button"
                      class="inline-flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <Info class="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    variant="secondary"
                    :show-arrow="true"
                    class="max-w-xs"
                  >
                    {{ t('participantTooltip') }}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <!-- Spacer to match trash button width for proper column alignment -->
          <div
            v-if="question.tag"
            class="hidden flex-shrink-0 sm:block"
            style="width: 44px"
          ></div>
        </div>
        <!-- Fields row -->
        <div class="flex w-full flex-col items-stretch gap-3 self-stretch sm:flex-row sm:items-end">
          <div class="min-w-0 flex-1">
            <ConnectedFieldSelect
              :model-value="getQuestionTag(question) || null"
              :tags="tags"
              :section-type="sectionType"
              :questionnaire-law-field="questionnaireLawField"
              @update:model-value="(value) => setQuestionTag(question, value || '')"
            />
          </div>
          <div
            v-if="isParticipantSection"
            class="min-w-0 flex-1"
          >
            <SelectField
              v-model="question.tag_relationship"
              :options="participantTypeOptions"
              :is-filterable="true"
              :disabled="isTagRelationshipReadonly"
              name="tag_relationship"
              :select-option-text="t('participant') + '...'"
              class="self-stretch"
            />
          </div>
          <!-- tag_number is now auto-managed: 1 for regular sections, instanceNumber for participant sections -->
          <div
            v-if="question.tag"
            class="flex-shrink-0"
          >
            <button
              type="button"
              class="inline-flex h-10 items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm
                font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                disabled:pointer-events-none disabled:opacity-50"
              @click="question.tag = ''"
            >
              <TrashIcon class="size-4 shrink-0" />
            </button>
          </div>
        </div>
      </div>

      <!-- Question Title -->
      <div class="flex w-full flex-col items-start justify-start gap-1">
        <label class="text-sm font-medium text-gray-700">
          {{ t('questionTitle') }}
        </label>
        <div class="w-full">
          <InputField
            v-model="question.label"
            name="question_title"
            :placeholder="t('questionTitle')"
          />
        </div>
      </div>

      <!-- Help Text -->
      <div class="flex w-full flex-col items-start justify-start gap-1">
        <div class="flex flex-col gap-0.5">
          <label class="text-sm font-medium text-gray-700">
            {{ t('helpText') }}
          </label>
          <span class="text-xs text-gray-500">
            {{ t('helpTextTooltip') }}
          </span>
        </div>
        <div class="w-full">
          <TextAreaField
            v-model="question.help"
            name="help"
            :placeholder="t('helpText')"
            rows="3"
          />
        </div>
      </div>

      <!-- Input Type -->
      <div class="flex w-full flex-col items-start justify-start gap-1 self-stretch">
        <SelectField
          v-model="question.type"
          name="input_type"
          :options="inputTypeOptions"
          :placeholder="t('notSelected')"
          class="self-stretch"
          :label="t('inputType')"
        >
          <template #button-contents="{ selectedOption }">
            <div class="flex min-w-0 items-center gap-2">
              <component
                :is="selectedOption?.icon"
                v-if="selectedOption?.icon"
                class="size-4"
              />
              <span class="truncate text-gray-800">
                {{ selectedOption?.title || t('notSelected') }}
              </span>
            </div>
          </template>
          <template #option-contents="{ option }">
            <div class="flex w-full items-center justify-between">
              <div class="flex items-center gap-2">
                <component
                  :is="option.icon"
                  v-if="option.icon"
                  class="size-4"
                />
                <span class="text-gray-800">
                  {{ option.title }}
                </span>
              </div>
              <div
                v-if="(option as InputTypeOption).supportsLogicJump"
                class="flex items-center gap-1"
              >
                <div
                  class="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium
                    text-green-700"
                >
                  <CheckCircle2 class="h-3 w-3" />
                  {{ t('supportsLogicJump') }}
                </div>
              </div>
            </div>
          </template>
        </SelectField>
      </div>

      <!-- Question-specific options based on field type -->
      <div
        v-if="question.type === FieldTypeEnum.YESNO"
        class="flex w-full flex-col items-start justify-start gap-2 self-stretch"
      >
        <div class="flex items-center justify-start gap-2 self-stretch">
          <!-- Drag Handle -->
          <div class="relative h-4 w-4 cursor-move overflow-hidden">
            <GripHorizontal class="absolute h-4 w-4" />
          </div>
          <div class="flex-1">
            <InputField
              :name="`choice-yes-0`"
              :model-value="t('yes')"
              class="w-full justify-start font-['Inter'] text-sm font-normal leading-tight text-gray-800"
              :placeholder="t('yes')"
              :disabled="true"
              @update:model-value="() => {}"
            />
          </div>
          <div class="flex-1">
            <SelectField
              :name="`jump-to-yes-0`"
              :options="jumpToOptions"
              :model-value="question.jump_to && question.jump_to[0] ? question.jump_to[0].uuid : ''"
              :disabled="!isJumpToEnabled"
              class="w-full justify-start font-['Inter'] text-sm font-normal leading-tight text-gray-800"
              :is-filterable="true"
              @update:model-value="(value) => updateJumpTo(value, 0)"
            />
          </div>
          <div
            class="flex w-8 cursor-not-allowed items-center justify-center overflow-hidden rounded-md bg-gray-100 p-1.5
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
          >
            <TrashIcon class="h-4 w-4 text-gray-300" />
          </div>
        </div>
        <div class="flex items-center justify-start gap-2 self-stretch">
          <!-- Drag Handle -->
          <div class="relative h-4 w-4 cursor-move overflow-hidden">
            <GripHorizontal class="absolute h-4 w-4" />
          </div>
          <div class="flex-1">
            <InputField
              :name="`choice-no-1`"
              :model-value="t('no')"
              class="w-full justify-start font-['Inter'] text-sm font-normal leading-tight text-gray-800"
              :placeholder="t('no')"
              :disabled="true"
              @update:model-value="() => {}"
            />
          </div>
          <div class="flex-1">
            <SelectField
              :name="`jump-to-no-1`"
              :options="jumpToOptions"
              :model-value="question.jump_to && question.jump_to[1] ? question.jump_to[1].uuid : ''"
              :disabled="!isJumpToEnabled"
              class="w-full justify-start font-['Inter'] text-sm font-normal leading-tight text-gray-800"
              :is-filterable="true"
              @update:model-value="(value) => updateJumpTo(value, 1)"
            />
          </div>
          <div
            class="flex w-8 cursor-not-allowed items-center justify-center overflow-hidden rounded-md bg-gray-100 p-1.5
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
          >
            <TrashIcon class="h-4 w-4 text-gray-300" />
          </div>
        </div>
        <div
          v-if="showLogicJump"
          class="inline-flex items-center justify-between self-stretch"
        >
          <div class="justify-start font-['Inter'] text-sm font-normal leading-tight text-gray-400">
            {{ t('switchToMultipleChoice') }}
          </div>
          <div class="flex items-center justify-start gap-2">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('logicJump') }}
            </div>
            <Switch
              :checked="isJumpToEnabled"
              @update:checked="handleSwitchChange"
            />
          </div>
        </div>
      </div>

      <div
        v-if="question.type === FieldTypeEnum.MULTISELECT || question.type === FieldTypeEnum.SELECT"
        class="flex w-full flex-col items-start justify-start gap-2 self-stretch"
      >
        <div
          v-for="(option, index) in question.choices"
          :key="index"
          class="flex items-center justify-start gap-2 self-stretch transition-colors"
          :class="[
            dragOverOptionIndex === index ? 'rounded border-blue-200 bg-blue-50' : '',
            draggedOptionIndex === index ? 'opacity-50' : ''
          ]"
          draggable="true"
          @dragstart="onOptionDragStart($event, index)"
          @dragover="onOptionDragOver($event, index)"
          @dragleave="onOptionDragLeave"
          @drop="onOptionDrop($event, index)"
          @dragend="onOptionDragEnd"
        >
          <div class="relative h-4 w-4 cursor-move overflow-hidden">
            <GripHorizontal class="absolute h-4 w-4" />
          </div>

          <div class="flex-1">
            <InputField
              :name="`choice-${index}`"
              :model-value="option"
              class="w-full justify-start font-['Inter'] text-sm font-normal leading-tight text-gray-800"
              :placeholder="t('choicePlaceholder', { number: index + 1 })"
              @update:model-value="(value) => updateOption(value, index)"
            />
          </div>
          <div class="flex-1">
            <SelectField
              :name="`jump-to-choice-${index}`"
              :options="jumpToOptions"
              :model-value="
                question.jump_to && question.jump_to[index] ? question.jump_to[index].uuid : ''
              "
              :disabled="!isJumpToEnabled"
              class="w-full justify-start font-['Inter'] text-sm font-normal leading-tight text-gray-800"
              :is-filterable="true"
              @update:model-value="(value) => updateJumpTo(value, index)"
            />
          </div>
          <!-- Delete Button -->
          <div
            class="flex w-8 cursor-pointer items-center justify-center overflow-hidden rounded-md bg-white p-1.5
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
              hover:bg-gray-50"
            @click="removeOption(index)"
          >
            <TrashIcon class="h-4 w-4 text-gray-500" />
          </div>
        </div>
        <!-- Add Option Button -->
        <div class="inline-flex items-center justify-between self-stretch">
          <button
            class="inline-flex h-8 items-center justify-center overflow-hidden rounded-md p-1.5 hover:bg-gray-50"
            @click="addOption"
          >
            <div class="relative h-5 w-5 overflow-hidden">
              <PlusIcon class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
            </div>
            <div class="flex items-center justify-center gap-1.5 px-1.5">
              <div
                class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
              >
                {{ t('addOption') }}
              </div>
            </div>
          </button>
          <!-- Logic Jump Toggle -->
          <div
            v-if="question.type === FieldTypeEnum.SELECT"
            class="flex items-center justify-start gap-2"
          >
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('logicJump') }}
            </div>
            <Switch
              :checked="isJumpToEnabled"
              @update:checked="handleSwitchChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Dialog -->
    <QuestionPreviewDialog
      v-model:open="isPreviewOpen"
      :question="question"
      :section-uuid="sectionUuid"
    />
  </div>
</template>
